/**
 * Getypeerde toegang tot `/residences/:id`: de instellingen van één woning.
 *
 * Wat er in deze laag zit is de prijs. Dat maakt hem strenger dan de andere
 * clients: een bedrag dat verkeerd wordt gelezen of verkeerd wordt teruggestuurd,
 * verandert wat een gast betaalt. Twee gevolgen daarvan:
 *
 * 1. **Alles in hele centen** (regel 3). De invoervelden tonen euro's, maar de
 *    omrekening gebeurt op één plek — `centsFromInput` en `centsToInput` — en
 *    nergens anders. Een deling door honderd die op vier plekken staat, is een
 *    afrondingsfout die op drie ervan anders uitpakt.
 * 2. **Een onleesbaar bedrag is een fout, geen nul.** Een tarief dat als € 0,00
 *    op het scherm verschijnt omdat het antwoord afweek, is een tarief dat iemand
 *    ongemerkt publiceert.
 */
import { call, isRecord, readCount, readOptionalText, readText } from './http'
import type { Failure, Result } from './http'
import type { RequestOptions } from './api-client'

export type ResidenceFailure = Failure
export type ResidenceResult<T> = Result<T>

export const PAYMENT_MODES = ['online', 'manual'] as const
export type PaymentMode = (typeof PAYMENT_MODES)[number]

/** Hoe de toeristenbelasting wordt bepaald (migratie 0020). */
export const TOURIST_TAX_MODES = ['none', 'per_person_night', 'percentage', 'area'] as const
export type TouristTaxMode = (typeof TOURIST_TAX_MODES)[number]

/** De prijs- en verblijfsregels van één woning; alles in hele centen. */
export interface ResidencePricing {
  readonly currency: string
  readonly baseRateCents: number
  readonly cleaningFeeCents: number
  readonly depositCents: number
  readonly extraGuestFeeCents: number
  readonly guestsIncluded: number
  readonly minNights: number
  /** Null betekent: geen bovengrens. */
  readonly maxNights: number | null
  readonly weeklyDiscountPermille: number
  readonly monthlyDiscountPermille: number
  readonly maxGuests: number
  readonly paymentMode: PaymentMode
  readonly holdExpiryHours: number
  readonly touristTaxMode: TouristTaxMode
  readonly touristTaxCents: number
  readonly touristTaxPermille: number
  readonly touristTaxReference: string
}

export interface ResidenceContent {
  readonly locale: string
  readonly headline: string
  readonly summary: string
  readonly description: string
  readonly locationDescription: string
  readonly houseRules: string
  readonly arrivalInstructions: string
  readonly departureInstructions: string
  readonly paymentInstructions: string
  readonly metaTitle: string
  readonly metaDescription: string
}

/**
 * Waar de woning staat.
 *
 * Apart van `Residence` gehouden omdat het één samenhangend geheel is dat op één
 * scherm hoort: een adres zonder coördinaten zet geen speld op de kaart, en
 * coördinaten zonder adres zeggen een gast niets.
 */
export interface ResidenceLocation {
  readonly addressLine1: string
  readonly addressLine2: string
  readonly postalCode: string
  readonly locality: string
  readonly region: string
  readonly country: string
  /** Null betekent: nog niet op de kaart gezet. */
  readonly latitude: number | null
  readonly longitude: number | null
}

export interface Residence {
  readonly id: string
  readonly slug: string
  readonly name: string
  readonly status: string
  /** Staat deze woning in de publieke SiteShowroom-gids? Los van `status`. */
  readonly directoryListed: boolean
  readonly location: ResidenceLocation
  /** Null betekent: geen restitutie afgesproken. */
  readonly cancellationPolicyId: string | null
  /** De betaalkoppeling; null betekent geen. Bij `online` blokkeert dat publicatie. */
  readonly paymentAccountId: string | null
  readonly pricing: ResidencePricing
  readonly content: readonly ResidenceContent[]
  /** De sleutels van de gekozen voorzieningen; de namen komen uit de vertalingen. */
  readonly amenityKeys: readonly string[]
}

function isPaymentMode(value: unknown): value is PaymentMode {
  const known: readonly string[] = PAYMENT_MODES
  return typeof value === 'string' && known.includes(value)
}

function isTouristTaxMode(value: unknown): value is TouristTaxMode {
  const known: readonly string[] = TOURIST_TAX_MODES
  return typeof value === 'string' && known.includes(value)
}

/**
 * Een coördinaat. Null wanneer hij ontbreekt — dat is een geldige toestand: de
 * woning is dan nog niet op de kaart gezet.
 *
 * Niet `readCount`: een breukgetal is hier juist de bedoeling, en `0` mag geen
 * terugval zijn — nul graden noorderbreedte is een echte plek in de Golf van Guinee.
 */
function readCoordinate(source: Record<string, unknown>, field: string): number | null {
  const raw = source[field]
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : null
}

/** Een bedrag in centen; ontbreken of een niet-geheel getal is een fout. */
function readCents(source: Record<string, unknown>, field: string): number | null {
  const raw = source[field]
  return Number.isSafeInteger(raw) ? (raw as number) : null
}

function readContent(value: unknown): ResidenceContent | null {
  if (!isRecord(value)) {
    return null
  }
  const locale = readText(value, 'locale')
  if (locale === '') {
    return null
  }
  return {
    locale,
    headline: readText(value, 'headline'),
    summary: readText(value, 'summary'),
    description: readText(value, 'description'),
    locationDescription: readText(value, 'locationDescription'),
    houseRules: readText(value, 'houseRules'),
    arrivalInstructions: readText(value, 'arrivalInstructions'),
    departureInstructions: readText(value, 'departureInstructions'),
    paymentInstructions: readText(value, 'paymentInstructions'),
    metaTitle: readText(value, 'metaTitle'),
    metaDescription: readText(value, 'metaDescription'),
  }
}

function readResidence(payload: unknown): Residence | null {
  if (!isRecord(payload) || !isRecord(payload['residence'])) {
    return null
  }
  const source = payload['residence']

  const id = readText(source, 'id')
  const currency = readText(source, 'currency')
  const paymentMode = source['paymentMode']
  const baseRateCents = readCents(source, 'baseRateCents')
  const cleaningFeeCents = readCents(source, 'cleaningFeeCents')
  const depositCents = readCents(source, 'depositCents')
  const extraGuestFeeCents = readCents(source, 'extraGuestFeeCents')

  if (id === '' || currency === '' || !isPaymentMode(paymentMode)) {
    return null
  }
  if (
    baseRateCents === null ||
    cleaningFeeCents === null ||
    depositCents === null ||
    extraGuestFeeCents === null
  ) {
    return null
  }

  const maxNights = source['maxNights']
  const content: ResidenceContent[] = []
  if (Array.isArray(source['content'])) {
    for (const entry of source['content']) {
      const item = readContent(entry)
      if (item !== null) {
        content.push(item)
      }
    }
  }

  return {
    id,
    slug: readText(source, 'slug'),
    name: readText(source, 'name'),
    status: readText(source, 'status') as ResidenceStatus,
    directoryListed: source['directoryListed'] === true,
    location: {
      addressLine1: readText(source, 'addressLine1'),
      addressLine2: readText(source, 'addressLine2'),
      postalCode: readText(source, 'postalCode'),
      locality: readText(source, 'locality'),
      region: readText(source, 'region'),
      country: readText(source, 'country'),
      latitude: readCoordinate(source, 'latitude'),
      longitude: readCoordinate(source, 'longitude'),
    },
    cancellationPolicyId: readOptionalText(source, 'cancellationPolicyId'),
    paymentAccountId: readOptionalText(source, 'paymentAccountId'),
    content,
    amenityKeys: Array.isArray(source['amenityKeys'])
      ? source['amenityKeys'].filter((key): key is string => typeof key === 'string')
      : [],
    pricing: {
      currency,
      paymentMode,
      baseRateCents,
      cleaningFeeCents,
      depositCents,
      extraGuestFeeCents,
      guestsIncluded: readCount(source, 'guestsIncluded'),
      minNights: readCount(source, 'minNights'),
      maxNights: Number.isSafeInteger(maxNights) ? (maxNights as number) : null,
      weeklyDiscountPermille: readCount(source, 'weeklyDiscountPermille'),
      monthlyDiscountPermille: readCount(source, 'monthlyDiscountPermille'),
      maxGuests: readCount(source, 'maxGuests'),
      holdExpiryHours: readCount(source, 'holdExpiryHours'),
      // Een onbekende modus wordt 'none' en niet geraden: belasting heffen die
      // niemand heeft ingesteld is erger dan er geen te tonen.
      touristTaxMode: isTouristTaxMode(source['touristTaxMode'])
        ? source['touristTaxMode']
        : 'none',
      touristTaxCents: readCents(source, 'touristTaxCents') ?? 0,
      touristTaxPermille: readCount(source, 'touristTaxPermille'),
      touristTaxReference: readText(source, 'touristTaxReference'),
    },
  }
}

export async function fetchResidence(
  id: string,
  options: RequestOptions = {},
): Promise<ResidenceResult<Residence>> {
  return call('GET', `/residences/${encodeURIComponent(id)}`, undefined, readResidence, options)
}

/** Alleen wat is meegestuurd wordt gewijzigd; ontbreken betekent "niet aanraken". */
export type ResidencePatch = Partial<{
  /**
   * De naam van de woning.
   *
   * Stond hier niet, terwijl de API hem wél accepteert — met als gevolg dat
   * `{{residence_name}}` in elk bericht stond en nergens te wijzigen was. De naam is
   * niet per taal: hij hoort bij de woning en niet bij de tekst.
   *
   * Het webadres verandert er niet van; de slug staat vast vanaf het aanmaken, want
   * een adres dat meebeweegt met een naam breekt elke link die een gast al heeft.
   */
  name: string
  /**
   * De publicatiestatus.
   *
   * Ontbrak hier, terwijl de API hem accepteert — met als gevolg dat een woning nooit
   * live kon vanaf het dashboard. De pilotwoning staat alleen gepubliceerd omdat de
   * seed dat zo heeft gezet (§17.37).
   */
  status: ResidenceStatus
  baseRateCents: number
  cleaningFeeCents: number
  depositCents: number
  extraGuestFeeCents: number
  guestsIncluded: number
  minNights: number
  maxNights: number | null
  weeklyDiscountPermille: number
  monthlyDiscountPermille: number
  maxGuests: number
  paymentMode: PaymentMode
  holdExpiryHours: number
  touristTaxMode: TouristTaxMode
  touristTaxCents: number
  touristTaxPermille: number
  touristTaxReference: string
  cancellationPolicyId: string | null
  paymentAccountId: string | null
  directoryListed: boolean
  addressLine1: string
  addressLine2: string
  postalCode: string
  locality: string
  region: string
  country: string
  latitude: number | null
  longitude: number | null
}>

export async function updateResidence(
  id: string,
  patch: ResidencePatch,
): Promise<ResidenceResult<Residence>> {
  return call('PATCH', `/residences/${encodeURIComponent(id)}`, patch, readResidence)
}

export type ContentPatch = Partial<Omit<ResidenceContent, 'locale'>>

export async function saveContent(
  id: string,
  locale: string,
  patch: ContentPatch,
): Promise<ResidenceResult<true>> {
  return call(
    'PUT',
    `/residences/${encodeURIComponent(id)}/content/${encodeURIComponent(locale)}`,
    patch,
    () => true,
  )
}

// ---------------------------------------------------------------------------
// Bedragen
// ---------------------------------------------------------------------------
//
// Eén plek voor de omrekening tussen wat de gebruiker typt en wat de database
// bewaart. Verspreid over de componenten wordt dit vroeg of laat drie keer net
// iets anders geschreven, en dan verschilt een tarief een cent per scherm.

/** Centen naar het invoerveld: 12500 wordt "125.00". */
export function centsToInput(cents: number): string {
  return (cents / 100).toFixed(2)
}

/**
 * Wat er is getypt naar hele centen, of `null` wanneer het geen bedrag is.
 *
 * Zowel de punt als de komma wordt geaccepteerd: een Nederlandstalige gebruiker
 * typt "125,50" en hoort daar geen foutmelding voor te krijgen. Afronden gebeurt
 * op de dichtstbijzijnde cent, want een bedrag met drie decimalen is een typefout
 * en geen prijs.
 */
export function centsFromInput(value: string): number | null {
  const normalized = value.trim().replace(',', '.')
  if (normalized === '' || !/^\d+(\.\d{0,2})?$/.test(normalized)) {
    return null
  }
  return Math.round(Number(normalized) * 100)
}

/** Promille naar een percentage voor het invoerveld: 75 wordt "7.5". */
export function permilleToInput(permille: number): string {
  return String(permille / 10)
}

/** Een percentage naar promille; `null` wanneer het geen getal is. */
export function permilleFromInput(value: string): number | null {
  const normalized = value.trim().replace(',', '.')
  if (normalized === '' || !/^\d+(\.\d)?$/.test(normalized)) {
    return null
  }
  const permille = Math.round(Number(normalized) * 10)
  return permille >= 0 && permille <= 1000 ? permille : null
}

/** Vertaalsleutel bij een mislukte aanroep. */
export function residenceErrorMessageKey(failure: ResidenceFailure): string {
  switch (failure.code) {
    case 'validation_failed':
      return 'dashboard.settings.error.rejected'
    case 'not_found':
      return 'dashboard.settings.error.notFound'
    case 'forbidden':
      return 'dashboard.settings.error.forbidden'
    case 'conflict':
      return 'dashboard.settings.error.conflict'
    default:
      return 'dashboard.settings.error.unavailable'
  }
}

// ---------------------------------------------------------------------------
// Voorzieningen
// ---------------------------------------------------------------------------

export interface Amenity {
  readonly key: string
  readonly category: string
  readonly sortOrder: number
}

/**
 * De catalogus waaruit een woning kan kiezen.
 *
 * Platformdata, dus niet per residence opgehaald. De naam van een voorziening staat
 * er niet bij: die is een vertaalsleutel (`guest.amenities.items.<key>`) en hoort in
 * de vertaalbestanden (regel 6).
 */
export async function listAmenityCatalogue(
  options: RequestOptions = {},
): Promise<ResidenceResult<readonly Amenity[]>> {
  return call(
    'GET',
    '/amenities',
    undefined,
    (payload) => {
      if (!isRecord(payload) || !Array.isArray(payload['amenities'])) {
        return null
      }
      const amenities: Amenity[] = []
      for (const entry of payload['amenities']) {
        if (!isRecord(entry)) {
          continue
        }
        const key = readText(entry, 'key')
        if (key === '') {
          continue
        }
        amenities.push({
          key,
          category: readText(entry, 'category'),
          sortOrder: readCount(entry, 'sortOrder'),
        })
      }
      return amenities
    },
    options,
  )
}

/** Vervangt de voorzieningen van een woning; de lijst is de volledige nieuwe set. */
export async function saveAmenities(
  id: string,
  amenityKeys: readonly string[],
): Promise<ResidenceResult<readonly string[]>> {
  return call(
    'PUT',
    `/residences/${encodeURIComponent(id)}/amenities`,
    { amenityKeys },
    (payload) => {
      if (!isRecord(payload) || !Array.isArray(payload['amenityKeys'])) {
        return null
      }
      return payload['amenityKeys'].filter((key): key is string => typeof key === 'string')
    },
  )
}

// ---------------------------------------------------------------------------
// Publicatie
// ---------------------------------------------------------------------------

export const RESIDENCE_STATUSES = ['draft', 'published', 'unlisted', 'archived'] as const
export type ResidenceStatus = (typeof RESIDENCE_STATUSES)[number]

/** Eén eis uit de publicatiechecklist. */
export interface PublicationRequirement {
  readonly key: string
  readonly met: boolean
  readonly detail: string | null
}

export interface PublicationState {
  readonly status: ResidenceStatus
  readonly requirements: readonly PublicationRequirement[]
}

/**
 * Wat er nog moet gebeuren voordat deze woning live kan.
 *
 * Komt van de API en wordt hier niet nagebouwd: het is dezelfde lijst die de
 * publicatiecontrole gebruikt, en twee kopieën lopen uiteen bij de eerste eis die
 * erbij komt.
 */
export async function fetchPublication(
  id: string,
  options: RequestOptions = {},
): Promise<ResidenceResult<PublicationState>> {
  return call(
    'GET',
    `/residences/${encodeURIComponent(id)}/publication`,
    undefined,
    (payload) => {
      if (!isRecord(payload) || !Array.isArray(payload['requirements'])) {
        return null
      }
      const status = payload['status']
      const known: readonly string[] = RESIDENCE_STATUSES
      if (typeof status !== 'string' || !known.includes(status)) {
        return null
      }
      const requirements: PublicationRequirement[] = []
      for (const entry of payload['requirements']) {
        if (!isRecord(entry)) {
          continue
        }
        const key = readText(entry, 'key')
        if (key === '') {
          continue
        }
        requirements.push({
          key,
          met: entry['met'] === true,
          detail: readOptionalText(entry, 'detail'),
        })
      }
      return { status: status as ResidenceStatus, requirements }
    },
    options,
  )
}
